export const PLUGIN_MISSION_TRIP = "mission_trip";
export const PLUGIN_SERAGAM = "seragam";

export const getFormDefinition = (event) =>
  event && event.form_definition && event.form_definition.registration_schema
    ? event.form_definition
    : null;

export const getPluginKey = (event) => {
  const definition = getFormDefinition(event);
  return definition ? definition.plugin_key : null;
};

export const hasDynamicForm = (event) => !!getFormDefinition(event);

export const localeKey = (language) =>
  language === "en" || language === "eng" ? "en" : "id";

export const localizedText = (value, language, fallback = "") => {
  if (value && typeof value === "object") {
    const locale = localeKey(language);
    return value[locale] || value.id || value.en || fallback;
  }
  return value || fallback;
};

export const getSessionMetadata = (session) => {
  if (!session || !session.behavior_metadata) return {};
  if (typeof session.behavior_metadata === "object") {
    return session.behavior_metadata;
  }
  try {
    return JSON.parse(session.behavior_metadata) || {};
  } catch (error) {
    return {};
  }
};

const packageLabel = (definition, packageType, language) => {
  const options =
    (definition &&
      definition.event_setup_schema &&
      definition.event_setup_schema.package_types) ||
    [];
  const option = options.find((item) => item.value === packageType);
  if (option) return localizedText(option.label, language, option.value);
  return packageType === "mealbox" ? "Mealbox Only" : "Paket Akomodasi";
};

export const getSessionContext = (definition, session, language = "ind") => {
  const metadata = getSessionMetadata(session);
  const sessionName = (session && session.name) || "";
  let packageType = metadata.package_type || null;
  let size = metadata.size || null;
  let gender = metadata.gender || null;

  if (definition && definition.plugin_key === PLUGIN_MISSION_TRIP && !packageType) {
    packageType = /meal\s*box|nasi\s*kotak/i.test(sessionName)
      ? "mealbox"
      : "accommodation";
  }
  if (definition && definition.plugin_key === PLUGIN_SERAGAM && !size) {
    const standardMatch = sessionName.match(/^Size\s+(.+)\s+\[([^\]]+)\]$/i);
    const customMatch = sessionName.match(/^Custom Size.+\[([^\]]+)\]$/i);
    if (standardMatch) {
      size = standardMatch[1].trim();
      gender = standardMatch[2].trim();
    } else if (customMatch) {
      size = "custom";
      gender = customMatch[1].trim();
    }
  }

  return {
    session_name: sessionName,
    package_type: packageType,
    package_label: packageType
      ? packageLabel(definition, packageType, language)
      : null,
    size,
    gender,
    is_custom_size: size === "custom",
  };
};

const pathValue = (values, path) =>
  String(path || "")
    .split(".")
    .reduce(
      (current, segment) =>
        current && Object.prototype.hasOwnProperty.call(current, segment)
          ? current[segment]
          : undefined,
      values
    );

export const conditionMatches = (condition, values) => {
  if (!condition || typeof condition !== "object") return true;
  if (Array.isArray(condition.all)) {
    return condition.all.every((item) => conditionMatches(item, values));
  }
  if (Array.isArray(condition.any)) {
    return condition.any.some((item) => conditionMatches(item, values));
  }

  const actual = pathValue(values, condition.source);
  const expected = condition.value;
  switch (condition.operator || "equals") {
    case "not_equals":
      return actual !== expected;
    case "in":
      return Array.isArray(expected) && expected.includes(actual);
    case "not_in":
      return !Array.isArray(expected) || !expected.includes(actual);
    case "truthy":
      return !!actual;
    case "falsy":
      return !actual;
    case "contains":
      return Array.isArray(actual)
        ? actual.includes(expected)
        : String(actual || "").includes(String(expected || ""));
    default:
      return actual === expected;
  }
};

export const getVisibleFields = (definition, session, answers, language) => {
  const schema = definition && definition.registration_schema;
  const fields = (schema && schema.fields) || [];
  const context = getSessionContext(definition, session, language);
  return fields
    .filter((field) =>
      conditionMatches(field.visible_when, { answers: answers || {}, context })
    )
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
};

const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  value === false ||
  (Array.isArray(value) && value.length === 0);

const optionValues = (field) => (field.options || []).map((option) => String(option.value));

export const validateFormAnswers = (definition, session, answers, language) => {
  const errors = {};
  getVisibleFields(definition, session, answers, language).forEach((field) => {
    const value = (answers || {})[field.key];
    const label = localizedText(field.label, language, field.key);
    if (field.required && isEmpty(value)) {
      errors[field.key] = `${label} wajib diisi.`;
      return;
    }
    if (isEmpty(value)) return;

    if (["select", "radio"].includes(field.type) && !optionValues(field).includes(String(value))) {
      errors[field.key] = `${label} tidak valid.`;
      return;
    }
    if (
      field.type === "multiselect" &&
      (!Array.isArray(value) || value.some((item) => !optionValues(field).includes(String(item))))
    ) {
      errors[field.key] = `${label} tidak valid.`;
      return;
    }
    if (field.validation && field.validation.pattern) {
      try {
        if (!new RegExp(field.validation.pattern).test(String(value))) {
          errors[field.key] = localizedText(
            field.validation.message,
            language,
            `${label} tidak valid.`
          );
        }
      } catch (error) {
        errors[field.key] = `${label} memiliki aturan validasi yang tidak valid.`;
      }
    }
  });

  return { valid: Object.keys(errors).length === 0, errors };
};

export const getVisibleAnswers = (definition, session, answers, language) => {
  const output = {};
  getVisibleFields(definition, session, answers, language).forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(answers || {}, field.key)) {
      output[field.key] = answers[field.key];
    }
  });
  return output;
};

export const formatAnswerValue = (field, value, language) => {
  if (value === true) return localeKey(language) === "en" ? "Yes" : "Ya";
  if (value === false) return localeKey(language) === "en" ? "No" : "Tidak";
  if (Array.isArray(value)) {
    return value
      .map((item) => formatAnswerValue(field, item, language))
      .join(", ");
  }
  const option = (field.options || []).find(
    (item) => String(item.value) === String(value)
  );
  return option ? localizedText(option.label, language, value) : value;
};

export const getAnswerDetails = (
  definition,
  session,
  answers,
  language,
  displayTarget = "ticket"
) =>
  getVisibleFields(definition, session, answers, language)
    .filter(
      (field) => !field.display || field.display[displayTarget] !== false
    )
    .filter((field) => !isEmpty((answers || {})[field.key]))
    .map((field) => ({
      key: field.key,
      label: localizedText(field.label, language, field.key),
      value: formatAnswerValue(field, answers[field.key], language),
    }));

export const getSeragamSize = (session) => {
  const context = getSessionContext({ plugin_key: PLUGIN_SERAGAM }, session);
  return context.size || (session && session.name) || "";
};

export const getSeragamGender = (session) => {
  const context = getSessionContext({ plugin_key: PLUGIN_SERAGAM }, session);
  return context.gender || "Pilihan Ukuran";
};
